import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AiOutlineClose } from "react-icons/ai";
import { useNavigate } from "react-router-dom";

const ProductSpecifications = ({ product, isOpen, onClose }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          role="dialog"
          aria-labelledby="product-title"
          aria-modal="true"
          tabIndex={-1}
        >
          <motion.div
            className="bg-white w-full max-w-5xl max-h-[90vh] p-6 md:p-8 rounded-lg shadow-lg overflow-y-auto relative my-auto"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-600 hover:text-gray-900 z-10"
              aria-label="Close modal"
            >
              <AiOutlineClose size={28} />
            </button>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 items-start">
              {/* Product Image */}
              <div className="w-full flex justify-center md:justify-start">
                <div className="w-full max-w-sm lg:max-w-md rounded-2xl border border-gray-200 bg-gray-50 p-3 shadow-sm">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-auto max-h-[380px] object-contain rounded-xl shadow-sm"
                  />
                </div>
              </div>

              {/* Product Details */}
              <div className="w-full space-y-5">
                <div className="space-y-2 pr-6">
                  <h2 id="product-title" className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                    {product.name}
                  </h2>
                  <p className="text-sm md:text-base text-gray-600 leading-relaxed">{product.description}</p>
                </div>

                {/* Specifications Section */}
                {product.specifications && (
                  <section aria-labelledby="specifications-heading" className="rounded-2xl border border-green-200 bg-green-50/80 p-4 md:p-5 shadow-sm">
                    <h3 id="specifications-heading" className="text-lg md:text-xl font-semibold text-green-800 mb-3">Specifications</h3>
                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                      <table className="w-full border-collapse text-sm md:text-base">
                        <thead>
                          <tr className="bg-gray-100 text-left text-gray-700">
                            <th className="p-3 border-b border-gray-200 font-semibold">Property</th>
                            <th className="p-3 border-b border-gray-200 font-semibold">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(product.specifications).map(([key, value]) => (
                            <tr key={key} className="align-top odd:bg-white even:bg-gray-50">
                              <td className="p-3 border-b border-gray-200 font-medium text-gray-700">{key}</td>
                              <td className="p-3 border-b border-gray-200 text-gray-600">
                                {Array.isArray(value) ? value.join(", ") : value}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}

                {/* Show Categories Button */}
                {product.name === "SS Screw" && (
                  <div className="flex justify-start">
                    <button
                      onClick={() => navigate("/ss-screw")}
                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
                    >
                      Categories
                    </button>
                  </div>
                )}

                {/* Size & Length Table */}
                {product.sizes && product.sizes.length > 0 && (
                  <section aria-labelledby="sizes-heading" className="rounded-2xl border border-blue-200 bg-blue-50/80 p-4 md:p-5 shadow-sm">
                    <h3 id="sizes-heading" className="text-lg md:text-xl font-semibold text-blue-800 mb-3">Size & Length</h3>
                    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                      <table className="w-full border-collapse text-sm md:text-base">
                        <thead>
                          <tr className="bg-gray-100 text-left text-gray-700">
                            <th className="p-3 border-b border-gray-200 font-semibold">Size</th>
                            <th className="p-3 border-b border-gray-200 font-semibold">Length</th>
                          </tr>
                        </thead>
                        <tbody>
                          {product.sizes.map((size, index) => (
                            <tr key={index} className="align-top odd:bg-white even:bg-gray-50">
                              <td className="p-3 border-b border-gray-200 font-medium text-gray-700">{size?.Size || "N/A"}</td>
                              <td className="p-3 border-b border-gray-200 text-gray-600">
                                {Array.isArray(size?.Lengths) ? size.Lengths.join(", ") : "N/A"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </section>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProductSpecifications;